import {FOBBetslip, canonicalFobPairOrder, resolveFobCurrentPick} from "features/betslip/fob.service";
import {BettingTypesMap} from "features/core/definitions";
import {fobOddsEntry} from "features/betslip/lib/selection-guard";

/** Seçimde saklanan sıra: kullanıcı hangi yuvaya hangi atı bastıysa o. */
function pickOrder(firstNo: any, secondNo: any): string[] {
    return [String(firstNo), String(secondNo)];
}

export function buildPairOddsKey(bettingType: any, firstNo: any, secondNo: any): string {
    const suffix = BettingTypesMap[Number(bettingType)]?.suffix ?? '';
    return `${canonicalFobPairOrder(bettingType, firstNo, secondNo).join('-')}${suffix}`;
}

/** Tek atlı türlerde pazar anahtarı: at no + türün soneki ("5", "5T2"). */
export function buildSingleOddsKey(bettingType: any, horseNo: any): string {
    return `${horseNo}${BettingTypesMap[Number(bettingType)]?.suffix ?? ''}`;
}

function hasLiveOdds(entry: any): boolean {
    if (entry?.show === false || entry?.bettable === false) return false;
    return !!((entry?.win_odds && Number(entry.win_odds) > 1) || entry?.status === 'o');
}

/** Bahis tablosundan bir ata basıldığında çalışır; iki aşamalı seçimi de yönetir. */
export function pickFobHorse(hippodrome: any, run: any, horse: any, bettingType: any) {
    if (!hippodrome?.show_fob) return;
    if (horse?.run_status === false) return;

    const bettingTypeId = Number(bettingType);
    const horseNo = String(horse.no);
    const isPairPick = !!BettingTypesMap[bettingTypeId]?.isFobPairPick;

    if (!isPairPick) {
        const oddKey = buildSingleOddsKey(bettingTypeId, horseNo);
        if (!hasLiveOdds(fobOddsEntry(run, bettingTypeId, oddKey))) return;
        FOBBetslip.toggle({hippodrome, run, bettingType: bettingTypeId, oddKey, horseNos: [horseNo]});
        return;
    }

    const pick = resolveFobCurrentPick(hippodrome, run, bettingTypeId);

    if (String(pick.first) === horseNo || String(pick.second) === horseNo) {
        if (pick.first !== null && pick.second !== null) {
            FOBBetslip.remove({
                hippodrome, run, bettingType: bettingTypeId,
                oddKey: buildPairOddsKey(bettingTypeId, pick.first, pick.second),
                horseNos: pickOrder(pick.first, pick.second),
            });
            const otherNo = String(pick.first) === horseNo ? pick.second : pick.first;
            FOBBetslip.setPendingPick({hippodrome, run, bettingType: bettingTypeId, first: otherNo, second: null});
        } else {
            FOBBetslip.clearPendingPick(hippodrome, run, bettingTypeId);
        }
        return;
    }

    // İkinci at: çifti tamamla. İlk at: yarım seçim olarak beklet.
    if (pick.first !== null) {
        const nextKey = buildPairOddsKey(bettingTypeId, pick.first, horseNo);
        if (!hasLiveOdds(run.event_odds?.[nextKey])) return;
        FOBBetslip.clearPendingPick(hippodrome, run, bettingTypeId);
        FOBBetslip.add({
            hippodrome, run, bettingType: bettingTypeId,
            oddKey: nextKey,
            horseNos: pickOrder(pick.first, horseNo),
        });
        return;
    }

    FOBBetslip.setPendingPick({hippodrome, run, bettingType: bettingTypeId, first: horseNo, second: null});
}

function pickSlotNo(value: any): string | null {
    return value === null || value === undefined ? null : String(value);
}

/**
 * Çift atlı türlerde (İkili / Sıralı İkili) sekmedeki kolon bir YUVA: "1. At" sekmesinde basılan
 * at ilk yuvaya, "2. At"ta basılan ikinci yuvaya yazılır. Yuvaya yeni at basmak öncekinin yerine
 * geçer; yuvadaki ata yeniden basmak yuvayı boşaltır. Öteki yuvadaki at bu yuvaya yazılamaz
 * (tabloda kilitli çizilir, bkz. `isFobHorseLocked`). İki yuva dolunca çift kupona girer; servis
 * "bir koşuya bir bahis" kuralıyla eski çifti kendisi düşürür.
 *
 * `pickFobHorse`in kolon bilmeyen iki aşamalı akışından farkı: orada "2. At" sekmesindeyken
 * basılan ilk at da birinci yuvaya yazılıyor ve tabloda 1. yuvadaki at 2. sekmede de seçili
 * görünüyordu.
 */
export function pickFobHorseInSlot(hippodrome: any, run: any, horse: any, bettingType: any, column: any) {
    if (!hippodrome?.show_fob) return;
    if (horse?.run_status === false) return;

    const bettingTypeId = Number(bettingType);
    const horseNo = String(horse.no);
    const slot: 'first' | 'second' = Number(column) === 2 ? 'second' : 'first';
    const other: 'first' | 'second' = slot === 'first' ? 'second' : 'first';
    const pick = resolveFobCurrentPick(hippodrome, run, bettingTypeId);
    const slotNo = pickSlotNo(pick[slot]);
    const otherNo = pickSlotNo(pick[other]);

    if (otherNo === horseNo) return; // öteki yuvada: kilitli

    const nextSlotNo = slotNo === horseNo ? null : horseNo;
    const nextFirst = slot === 'first' ? nextSlotNo : otherNo;
    const nextSecond = slot === 'second' ? nextSlotNo : otherNo;

    if (nextFirst !== null && nextSecond !== null) {
        const nextKey = buildPairOddsKey(bettingTypeId, nextFirst, nextSecond);
        if (!hasLiveOdds(run.event_odds?.[nextKey])) return;
        FOBBetslip.clearPendingPick(hippodrome, run, bettingTypeId);
        FOBBetslip.add({
            hippodrome, run, bettingType: bettingTypeId,
            oddKey: nextKey,
            horseNos: pickOrder(nextFirst, nextSecond),
        });
        return;
    }

    // Çift tamamdı, bir yuva boşaltılıyor: kupondaki satır düşer, kalan at yarım seçim olur.
    if (slotNo !== null && otherNo !== null) {
        FOBBetslip.remove({
            hippodrome, run, bettingType: bettingTypeId,
            oddKey: buildPairOddsKey(bettingTypeId, pick.first, pick.second),
            horseNos: pickOrder(pick.first, pick.second),
        });
    }
    if (nextFirst === null && nextSecond === null) {
        FOBBetslip.clearPendingPick(hippodrome, run, bettingTypeId);
        return;
    }
    FOBBetslip.setPendingPick({hippodrome, run, bettingType: bettingTypeId, first: nextFirst, second: nextSecond});
}

/** "ORANLAR" panelinden hazır çift ("3-7"): iki yuva birden dolar; aynı çifte yeniden basmak kaldırır. */
export function pickFobPair(hippodrome: any, run: any, firstHorse: any, secondHorse: any, bettingType: any) {
    if (!hippodrome?.show_fob) return;
    if (firstHorse?.run_status === false || secondHorse?.run_status === false) return;

    const bettingTypeId = Number(bettingType);
    const oddKey = buildPairOddsKey(bettingTypeId, firstHorse.no, secondHorse.no);
    const horseNos = pickOrder(firstHorse.no, secondHorse.no);
    const existing = FOBBetslip.getAll().find((i) =>
        i.hippodrome._id === hippodrome._id && i.run.no === run.no && i.bettingType === bettingTypeId && i.oddKey === oddKey);
    if (existing) {
        FOBBetslip.remove(existing);
        return;
    }
    if (!hasLiveOdds(run.event_odds?.[oddKey])) return;
    FOBBetslip.clearPendingPick(hippodrome, run, bettingTypeId);
    FOBBetslip.add({hippodrome, run, bettingType: bettingTypeId, oddKey, horseNos});
}

/**
 * Bahis tablosunda atın seçili görünüp görünmeyeceği. Çift atlı türde `column` verilirse
 * yalnız o yuvaya bakılır: "1. At" sekmesinde ilk yuvadaki, "2. At"ta ikinci yuvadaki at seçili.
 */
export function isFobHorsePicked(hippodrome: any, run: any, horse: any, bettingType: any, column?: any): boolean {
    const horseNo = String(horse?.no ?? horse);
    const {first, second} = resolveFobCurrentPick(hippodrome, run, Number(bettingType));
    if (column !== undefined && !!BettingTypesMap[Number(bettingType)]?.isFobPairPick) {
        return String(Number(column) === 2 ? second : first) === horseNo;
    }
    return String(first) === horseNo || String(second) === horseNo;
}

/**
 * Çift atlı türde tablonun "Sabit Ganyan" hücresi ne göstersin: oran ÇİFTE ait, tek ata değil.
 * - `locked`: at öteki yuvada seçili (aynı at iki yuvaya yazılamaz).
 * - `pair`: öteki yuva dolu, bu atla kurulacak çiftin pazar kaydı (`entry`; yoksa kilit çizilir).
 * - `open` / `closed`: öteki yuva boş, henüz çift yok - at seçilebiliyor mu (tek at pazarı "o" mu).
 */
export function pairPickCellState(hippodrome: any, run: any, horse: any, bettingType: any, column: any):
    { kind: 'locked' | 'pair' | 'open' | 'closed'; entry?: any } {
    const typeId = Number(bettingType);
    const horseNo = String(horse?.no ?? horse);
    const {first, second} = resolveFobCurrentPick(hippodrome, run, typeId);
    const otherNo = pickSlotNo(Number(column) === 2 ? first : second);
    if (otherNo !== null) {
        if (otherNo === horseNo) {
            return {kind: 'locked'};
        }
        const key = Number(column) === 2
            ? buildPairOddsKey(typeId, otherNo, horseNo)
            : buildPairOddsKey(typeId, horseNo, otherNo);
        return {kind: 'pair', entry: run?.event_odds?.[key]};
    }
    return {kind: run?.event_odds?.[String(Number(horseNo))]?.status === 'o' ? 'open' : 'closed'};
}

/** Çift atlı türde at ÖTEKİ yuvada seçiliyse bu sekmede kilitli: aynı at iki yuvaya yazılamaz. */
export function isFobHorseLocked(hippodrome: any, run: any, horse: any, bettingType: any, column: any): boolean {
    if (!BettingTypesMap[Number(bettingType)]?.isFobPairPick) return false;
    const horseNo = String(horse?.no ?? horse);
    const {first, second} = resolveFobCurrentPick(hippodrome, run, Number(bettingType));
    const otherNo = Number(column) === 2 ? first : second;
    return otherNo !== null && otherNo !== undefined && String(otherNo) === horseNo;
}
